/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import cn from 'classnames';
import { useRouter } from 'next/router';

import { clearTimeoutIfExists } from 'libs/node';

import styles from './AIProductChatDemoSection.module.scss';

const SourceIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
    <path
      d="M1 4.4 4 7.5 9 1.2"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SendIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M12.833 1.167 6.417 7.583M12.833 1.167 8.75 12.833l-2.333-5.25-5.25-2.333z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

type Message = {
  role: 'user' | 'agent';
  avatar: string;
  body: ReactNode;
};

const MESSAGES: Message[] = [
  {
    role: 'user',
    avatar: 'N',
    body: 'Is Revolut Ltd still active? Who owns it and what are their latest numbers?',
  },
  {
    role: 'agent',
    avatar: 'R',
    body: (
      <>
        <strong>Revolut Ltd</strong> (Company No. 08804411) — <span className={styles.tag}>Active</span>
        <div className={styles.dataRow}>
          <div className={styles.dataChip}>
            <span className={styles.dataLabel}>Revenue</span>
            <span className={styles.dataValue}>£1.8B</span>
          </div>
          <div className={styles.dataChip}>
            <span className={styles.dataLabel}>Net Profit</span>
            <span className={styles.dataValue}>£344M</span>
          </div>
          <div className={styles.dataChip}>
            <span className={styles.dataLabel}>Employees</span>
            <span className={styles.dataValue}>8,200+</span>
          </div>
          <div className={styles.dataChip}>
            <span className={styles.dataLabel}>UBO</span>
            <span className={styles.dataValue}>N. Storonsky (51.2%)</span>
          </div>
        </div>
        <span className={styles.source}>
          <SourceIcon />
          UK Companies House · Updated 3 days ago
        </span>
      </>
    ),
  },
  {
    role: 'user',
    avatar: 'N',
    body: 'Now find me 10 fintech companies in Germany with revenue over €50M growing 20%+ YoY',
  },
  {
    role: 'agent',
    avatar: 'R',
    body: (
      <>
        Found <strong>14 matches</strong>. Here are the top 3:
        <div className={styles.list}>
          <div>
            <strong>1.</strong> N26 GmbH — €218M rev, +34% YoY <span className={styles.tag}>Active</span>
          </div>
          <div>
            <strong>2.</strong> Raisin GmbH — €112M rev, +28% YoY <span className={styles.tag}>Active</span>
          </div>
          <div>
            <strong>3.</strong> Scalable Capital — €87M rev, +41% YoY <span className={styles.tag}>Active</span>
          </div>
        </div>
        <span className={styles.source}>
          <SourceIcon />
          11 more results available · Export all to CSV ↗
        </span>
      </>
    ),
  },
  {
    role: 'user',
    avatar: 'N',
    body: "Show me N26's group structure and who their parent company is",
  },
  {
    role: 'agent',
    avatar: 'R',
    body: (
      <>
        <strong>N26 Group Structure:</strong>
        <div className={styles.tree}>
          N26 GmbH → owned 100% by <strong>N26 Group GmbH</strong> (Berlin)
          <br />
          └ N26 Bank GmbH (licensed subsidiary)
          <br />
          └ N26 Inc. (US subsidiary, New York)
          <br />└ N26 Ltd (UK subsidiary, London — <span className={styles.dissolved}>Dissolved</span>)
        </div>
        <span className={styles.source}>
          <SourceIcon />
          Handelsregister, DE · Updated 12 hours ago
        </span>
      </>
    ),
  },
];

export const AIProductChatDemoSection = () => {
  const { asPath } = useRouter();
  const [visibleCount, setVisibleCount] = useState(0);
  const [typing, setTyping] = useState(false);
  const [started, setStarted] = useState(false);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const updateFades = () => {
    const node = bodyRef.current;
    if (!node) {
      return;
    }

    setShowTopFade(node.scrollTop > 4);
    setShowBottomFade(node.scrollTop + node.clientHeight < node.scrollHeight - 4);
  };

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!started || visibleCount >= MESSAGES.length) {
      return;
    }

    const pause = visibleCount === 0 ? 300 : 500;
    const typingDuration = 900 + Math.random() * 1100;

    let showId: ReturnType<typeof setTimeout> | undefined;

    const pauseId = setTimeout(() => {
      setTyping(true);

      showId = setTimeout(() => {
        setTyping(false);
        setVisibleCount((c) => c + 1);
      }, typingDuration);
    }, pause);

    return () => {
      clearTimeout(pauseId);
      clearTimeoutIfExists(showId);
    };
  }, [visibleCount, started]);

  useEffect(() => {
    const node = bodyRef.current;
    if (!node) {
      return;
    }

    node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });

    const id = setTimeout(updateFades, 500);

    return () => {
      clearTimeout(id);
    };
  }, [visibleCount, typing]);

  const nextMsg = MESSAGES[visibleCount];

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={styles.grid} aria-hidden="true" />
      <div className={styles.blobA} aria-hidden="true" />
      <div className={styles.blobB} aria-hidden="true" />

      <div className={styles.box}>
        <div className={styles.header}>
          <div className={styles.headerAvatar}>
            <svg viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="14.5" stroke="#fff" strokeWidth={2} />
              <ellipse cx="16" cy="16" rx="8" ry="14.5" stroke="#fff" strokeWidth={1.5} />
              <path d="M2 11.5h28M2 20.5h28" stroke="#fff" strokeWidth={1.2} />
            </svg>
          </div>

          <div>
            <div className={styles.headerName}>Regis</div>
            <div className={styles.headerStatus}>
              <span className={styles.statusDot} />
              Online
            </div>
          </div>

          <div className={styles.headerLive}>Live demo</div>
        </div>

        <div className={styles.bodyWrap}>
          <div className={cn(styles.fadeTop, showTopFade && styles.fadeVisible)} />
          <div className={styles.body} ref={bodyRef} onScroll={updateFades}>
            {MESSAGES.slice(0, visibleCount).map((msg, idx) => (
              <div key={idx} className={cn(styles.msg, styles[`msg_${msg.role}`])}>
                <div className={styles.msgAvatar}>{msg.avatar}</div>
                <div className={styles.bubble}>{msg.body}</div>
              </div>
            ))}

            {typing && nextMsg && (
              <div className={cn(styles.msg, styles[`msg_${nextMsg.role}`])}>
                <div className={styles.msgAvatar}>{nextMsg.avatar}</div>
                <div className={cn(styles.bubble, styles.typing)}>
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
          </div>

          <div className={cn(styles.fadeBottom, showBottomFade && styles.fadeVisible)} />
        </div>

        <div className={styles.input}>
          <input type="text" placeholder="Ask about any company..." />
          <a href={`https://platform.globaldatabase.com/public/signup/regis?origin=${process.env.HOST}${asPath}`}>
            Send
            <SendIcon />
          </a>
        </div>
      </div>
    </div>
  );
};
